import { isEmptyUpdate } from '@streamlain/doc-publication-contract';
import {
  decodeUpdate,
  diffUpdate,
  encodeStateVectorFromUpdate,
  mergeUpdates,
} from 'yjs';

export function isEqualUint8Arrays(a: Uint8Array, b: Uint8Array) {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

/**
 *
 * @param local - local doc data
 * @param localSv - local doc state vector
 * @param remoteDiff - remote doc data diff with local doc state vector,
 * should calculated by `Y.diffUpdate(remoteDocData, localSv)`
 * @param remoteSv - remote doc state vector
 * @returns null if no diff, otherwise return the diff data
 */
export function docDiffUpdate(
  local: Uint8Array,
  localSv: Uint8Array,
  remoteDiff: Uint8Array,
  remoteSv: Uint8Array
) {
  // if localSv is not equal to remoteSv, return the diff data
  if (!isEqualUint8Arrays(localSv, remoteSv)) {
    return diffUpdate(local, remoteSv);
  }

  // localDiff is the deletedSet of local doc
  const localDiff = diffUpdate(local, localSv);

  // if localDiff is equal to remoteDiff, return null, means no diff
  if (isEqualUint8Arrays(localDiff, remoteDiff)) {
    return null;
  } else {
    // otherwise, return the diff data
    return diffUpdate(local, remoteSv);
  }
}

/**
 * Whether a remote update holds structs or deletions the local doc lacks.
 * Structs beyond the local state vector, including ones behind a gap, always
 * count. Deletions count unless merging them leaves the local delete set
 * unchanged. A non-canonical encoding can only report a change, never hide one.
 */
export function remoteUpdateChangesLocal(
  local: Uint8Array,
  remote: Uint8Array
): boolean {
  const localSv = encodeStateVectorFromUpdate(local);
  const remoteDiff = diffUpdate(remote, localSv);
  if (decodeUpdate(remoteDiff).structs.length) return true;
  return !isEqualUint8Arrays(
    diffUpdate(mergeUpdates([local, remoteDiff]), localSv),
    diffUpdate(local, localSv)
  );
}

/**
 * Whether a diff the remote computed against `localSv` changes the local doc.
 * A diff of equal states still carries the whole delete set: when it equals
 * the local one byte for byte nothing changes, otherwise the full check
 * decides. Docs the check cannot read count as changed, so they are written.
 */
export function remoteDiffChangesLocal(
  local: Uint8Array,
  localSv: Uint8Array,
  remoteDiff: Uint8Array
): boolean {
  if (isEmptyUpdate(remoteDiff)) return false;
  try {
    if (isEqualUint8Arrays(diffUpdate(local, localSv), remoteDiff))
      return false;
    return remoteUpdateChangesLocal(local, remoteDiff);
  } catch {
    return true;
  }
}
